import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, X } from "lucide-react";
import { useProducts, imgUrl } from "./useProducts";

const SHOW_MS = 7000;
const GAP_MS = 2 * 60 * 1000;
const FIRST_DELAY_MS = 8000;

const MEN = ["Yusuf", "Ibrahim", "Bilal", "Hamza", "Zayd", "Omar", "Ali", "Musa", "Idris", "Ayaan", "Rayyan", "Tariq", "Khalid", "Sulaiman", "Haroon", "Ismail", "Imran", "Adam", "Zakariya", "Abdullah", "Ahmed", "Mustafa", "Jafar", "Rizwan", "Faisal"];
const WOMEN = ["Maryam", "Aisha", "Fatima", "Zainab", "Khadija", "Hafsa", "Amina", "Safiya", "Noor", "Sumaya", "Layla", "Hana", "Ruqayya", "Asiya", "Iman", "Sara", "Yasmin", "Zahra", "Nadia", "Salma", "Huda", "Rania", "Mariam", "Aaliyah", "Inaya"];
const COUNTRIES = ["United States", "United Kingdom", "Canada", "France", "Germany", "Netherlands", "Australia", "UAE", "Saudi Arabia", "Qatar", "Turkey", "Malaysia", "Pakistan", "South Africa", "Sweden", "Belgium", "Ireland", "Singapore"];
const SIZES = ["S", "M", "L", "XL", "2XL"];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const shuffle = (arr) => arr.map((x) => [Math.random(), x]).sort((a, b) => a[0] - b[0]).map((x) => x[1]);
const isWomens = (p) => /sister|women|hijab/i.test(`${p.name} ${p.category}`);

const makeSale = (product) => {
  const option = pick(product.options);
  const color = product.colors?.length ? pick(product.colors).name : null;
  const woman = isWomens(product) || Math.random() < 0.5;
  return {
    id: `${Date.now()}-${Math.random()}`,
    name: woman ? pick(WOMEN) : pick(MEN),
    country: pick(COUNTRIES),
    product,
    piece: product.options.length > 1 ? `${product.name} · ${option.label}` : product.name,
    color,
    size: option.sized === false ? null : pick(SIZES),
    minutesAgo: 2 + Math.floor(Math.random() * 38),
  };
};

export const PurchasePopup = () => {
  const products = useProducts();
  const [sale, setSale] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const ready = useMemo(() => products.filter((p) => p.options?.length && p.remaining !== 0), [products]);

  useEffect(() => {
    if (!ready.length || dismissed) return;
    let hide, next, lastId = null;
    let queue = [];
    const nextProduct = () => {
      if (!queue.length) {
        queue = shuffle(ready);
        if (ready.length > 1 && queue[0]?.id === lastId) queue.push(queue.shift());
      }
      const p = queue.shift();
      lastId = p.id;
      return p;
    };
    const show = () => {
      setSale(makeSale(nextProduct()));
      hide = setTimeout(() => {
        setSale(null);
        next = setTimeout(show, GAP_MS);
      }, SHOW_MS);
    };
    const first = setTimeout(show, FIRST_DELAY_MS);
    return () => { clearTimeout(first); clearTimeout(hide); clearTimeout(next); };
  }, [ready, dismissed]);

  return (
    <AnimatePresence>
      {sale && (
        <motion.aside
          key={sale.id}
          data-testid="purchase-popup"
          initial={{ opacity: 0, x: -40, y: 10 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-5 left-4 sm:left-6 z-[60] w-[calc(100vw-2rem)] max-w-[340px] bg-[#ffffff]/95 backdrop-blur-md border border-[#e3e2dc] rounded-2xl shadow-[0_24px_60px_-24px_rgba(10,10,11,0.45)] p-3 flex items-center gap-3"
        >
          <div className="h-14 w-11 rounded-lg overflow-hidden bg-[#f3f2ec] shrink-0 flex items-center justify-center">
            {sale.product?.image ? <img src={imgUrl(sale.product.image)} alt="" className="h-full w-full object-cover object-top" /> : <ShoppingBag size={16} className="text-[#5f5e5a]" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm leading-snug">
              <span data-testid="purchase-popup-name" className="font-semibold">{sale.name}</span> from {sale.country} just pre-ordered
            </p>
            <p data-testid="purchase-popup-item" className="font-display font-extrabold uppercase text-[11px] leading-tight mt-0.5 line-clamp-2">{sale.piece}</p>
            <p className="font-mono-d text-[9px] uppercase tracking-[0.15em] text-[#5f5e5a] mt-1">
              {[sale.color, sale.size && `Size ${sale.size}`, `${sale.minutesAgo} min ago`].filter(Boolean).join(" · ")}
            </p>
          </div>
          <button data-testid="purchase-popup-close" onClick={() => { setDismissed(true); setSale(null); }} aria-label="Dismiss" className="self-start text-[#5f5e5a] hover:text-[#ff2a85]">
            <X size={14} />
          </button>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};
